import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, Layers } from 'lucide-react';
import type { DSRun } from '@/api/deep-signal-api';

// Friendly labels for known run types. Anything not in this map falls back to a
// humanized version of run_label, then to the sequence number, so new run types
// never render as a raw enum string or a blank.
const RUN_LABELS: Record<string, string> = {
  baseline: 'Baseline',
  're-measurement': 'Re-measurement',
  post_d10: 'Day 10',
  post_d30: 'Day 30',
  post_d60: 'Day 60',
};

export function runDisplayLabel(run: DSRun): string {
  // The first read after a series rebaseline is the series' new baseline,
  // whatever label it was launched with.
  if (run.series?.new_baseline) return 'New baseline';
  if (run.run_label && RUN_LABELS[run.run_label]) return RUN_LABELS[run.run_label];
  if (run.run_label) {
    return run.run_label.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return run.run_sequence_number != null ? `Run ${run.run_sequence_number}` : `Run ${run.id}`;
}

function runDate(run: DSRun): string {
  const d = run.completed_at || run.started_at;
  return d ? new Date(d).toLocaleDateString(undefined, { dateStyle: 'medium' }) : '';
}

const HOVER_BG = 'rgba(0,0,0,0.04)';

/**
 * Labeled run switcher for Deep Signal. Always tells you which run you're
 * viewing; lets you switch when more than one exists. With a single run it
 * renders as a static label (no dropdown), so the common case stays quiet.
 */
export function RunSelector({
  runs,
  selectedRunId,
  onSelect,
}: {
  runs: DSRun[];
  selectedRunId: number | null;
  onSelect: (runId: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const selected = runs.find((r) => r.id === selectedRunId) ?? null;
  if (!selected) return null;

  // Single run: static label, no dropdown.
  if (runs.length <= 1) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <Layers size={14} style={{ color: 'var(--muted-foreground)', flexShrink: 0 }} />
        <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--foreground)' }}>
          {runDisplayLabel(selected)}
        </span>
        {runDate(selected) && (
          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)' }}>
            · {runDate(selected)}
          </span>
        )}
      </div>
    );
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          border: '1px solid var(--border)',
          borderRadius: '8px',
          padding: '6px 10px',
          backgroundColor: 'var(--background)',
          cursor: 'pointer',
          fontSize: 'var(--text-sm)',
          color: 'var(--foreground)',
        }}
      >
        <Layers size={14} style={{ color: 'var(--muted-foreground)', flexShrink: 0 }} />
        <span style={{ fontWeight: 600 }}>{runDisplayLabel(selected)}</span>
        {runDate(selected) && (
          <span style={{ color: 'var(--muted-foreground)' }}>· {runDate(selected)}</span>
        )}
        <ChevronDown
          size={14}
          style={{
            color: 'var(--muted-foreground)',
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.15s',
          }}
        />
      </button>

      {open && (
        <div
          role="listbox"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            zIndex: 50,
            minWidth: '240px',
            backgroundColor: 'var(--background)',
            border: '1px solid var(--border)',
            borderRadius: '10px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            padding: '4px',
          }}
        >
          {runs.map((run) => {
            const isSel = run.id === selectedRunId;
            return (
              <button
                key={run.id}
                type="button"
                role="option"
                aria-selected={isSel}
                onClick={() => {
                  onSelect(run.id);
                  setOpen(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '12px',
                  width: '100%',
                  textAlign: 'left',
                  border: 'none',
                  backgroundColor: isSel ? HOVER_BG : 'transparent',
                  borderRadius: '7px',
                  padding: '8px 10px',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  if (!isSel) e.currentTarget.style.backgroundColor = HOVER_BG;
                }}
                onMouseLeave={(e) => {
                  if (!isSel) e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <span style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                  <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--foreground)' }}>
                    {runDisplayLabel(run)}
                  </span>
                  {runDate(run) && (
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)' }}>
                      {runDate(run)}
                    </span>
                  )}
                </span>
                {isSel && <Check size={15} style={{ color: 'var(--foreground)', flexShrink: 0 }} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
